import { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth, formatApiErrorDetail } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex justify-center">
          <span className="inline-flex rounded-[16px] bg-[#222222] px-9 py-6">
            <Logo />
          </span>
        </div>
        <form onSubmit={submit} className="rounded-[12px] border border-edge/70 bg-white p-8 shadow-xl shadow-black/10" data-testid="login-form">
          <h1 className="font-archivo text-2xl font-black tracking-tight text-cream" data-testid="login-heading">Beheerderslogin</h1>
          <p className="mt-2 text-sm text-faint">Log in om je formulieraanvragen te bekijken.</p>
          <div className="mt-6">
            <label htmlFor="login-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-faint">E-mail</label>
            <input id="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jij@4thscenery.nl" className="input-field" data-testid="login-email-input" />
          </div>
          <div className="mt-4">
            <label htmlFor="login-password" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-faint">Wachtwoord</label>
            <input id="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="input-field" data-testid="login-password-input" />
          </div>
          {error && (
            <p className="mt-4 rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
              {error}
            </p>
          )}
          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-[10px] bg-amber px-7 py-3.5 font-archivo text-base font-bold tracking-tight text-[#181510] transition-colors duration-300 hover:bg-amberdeep disabled:opacity-60"
            data-testid="login-submit-button"
          >
            <LogIn size={17} strokeWidth={2.5} />
            {loading ? "Inloggen..." : "Inloggen"}
          </motion.button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-faint transition-colors hover:text-amberdeep" data-testid="login-back-link">
            <ArrowLeft size={13} />
            Terug naar de site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
